import { resolveBreadcrumbSegmentKey } from "./breadcrumb-segment-keys.js";

function titleCaseSlug(segment) {
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
}

function walkNavNodes(items, ancestors = [], acc = []) {
  for (const item of items || []) {
    acc.push({ url: item.url || "", title: item.title, ancestors });
    if (Array.isArray(item.items) && item.items.length) {
      walkNavNodes(item.items, [...ancestors, item], acc);
    }
  }
  return acc;
}

/**
 * Resolve a static crumb label from the sidebar tree.
 * Detail tails (ObjectId after a leaf url) must not inherit the leaf title.
 */
export function resolveNavTitle(currentPath, navMain) {
  if (!currentPath || currentPath === "/") return null;
  const nodes = walkNavNodes(navMain);
  const exact = nodes.find((node) => node.url && node.url !== "#" && node.url === currentPath);
  if (exact?.title) return exact.title;

  const childPages = nodes.filter(
    (node) => node.url && node.url !== "#" && node.url.startsWith(`${currentPath}/`),
  );
  if (childPages.length === 0) return null;

  const ancestors = childPages[0].ancestors;
  for (let i = ancestors.length - 1; i >= 0; i -= 1) {
    const ancestor = ancestors[i];
    if (!ancestor?.title) continue;
    if (!ancestor.url || ancestor.url === "#" || currentPath === ancestor.url || currentPath.startsWith(`${ancestor.url}/`)) {
      return ancestor.title;
    }
  }
  return null;
}

function resolveStaticName({
  segment,
  pathSegments,
  index,
  currentPath,
  navMain,
  t,
}) {
  const specialKey = resolveBreadcrumbSegmentKey(segment, pathSegments, index);
  if (specialKey) return t(specialKey);

  const navTitle = resolveNavTitle(currentPath, navMain);
  if (navTitle) return navTitle;

  return titleCaseSlug(segment);
}

function appendTicketingSettingsTab(breadcrumb, currentPath, search, t) {
  const tab = new URLSearchParams(search).get("tab") || "general";
  const tabLabels = {
    general: t("ticketingSettingsTabs.general"),
    categories: t("ticketingSettingsTabs.categories"),
    sla: t("ticketingSettingsTabs.sla"),
    "approval-flows": t("ticketingSettingsTabs.assignmentTemplates", {
      defaultValue: t("ticketingSettingsTabs.approvalFlows", { defaultValue: "Assignment templates" }),
    }),
  };
  if (!tabLabels[tab]) return;
  breadcrumb[breadcrumb.length - 1].current = false;
  breadcrumb.push({
    name: tabLabels[tab],
    path: `${currentPath}?tab=${tab}`,
    current: true,
  });
}

/**
 * Header breadcrumbs.
 *
 * Name order for each URL segment:
 * 1. dynamicNames[segment] — entity display name (asset.assetName, ticket subject, …)
 * 2. contextual leftover i18n key
 * 3. sidebar nav title for this path prefix
 * 4. title-cased slug
 *
 * ObjectIds are never mapped in the leftover table. Until the detail page calls
 * setDynamicName(id, name), the last crumb is the title-cased segment; after
 * load it becomes the record name (e.g. "qrtdf"), never the raw ObjectId label.
 */
export function buildBreadcrumb({
  pathname,
  search = "",
  navMain = [],
  dynamicNames = {},
  t,
}) {
  const pathSegments = String(pathname || "").split("/").filter(Boolean);
  const breadcrumb = [];
  let currentPath = "";

  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;

    if (segment === "incidents" && pathSegments[0] === "incident-management" && index < pathSegments.length - 1) {
      return;
    }

    let defaultName = resolveStaticName({
      segment,
      pathSegments,
      index,
      currentPath,
      navMain,
      t,
    });

    if (pathSegments[0] === "my-work" && index === 1) {
      defaultName = t(`myWorkKinds.${segment}`, { defaultValue: defaultName });
    }
    if (pathSegments[0] === "my-work" && index === 2) {
      defaultName = t("detail", { defaultValue: "Detail" });
    }

    const name = dynamicNames[segment] || defaultName;

    if (
      (segment === "surveys" || segment === "automations")
      && pathSegments[0] === "definitions"
      && !pathSegments.includes("integrations")
    ) {
      breadcrumb.push({
        name: t("integrations"),
        path: "/definitions/integrations/connectors",
        current: false,
      });
    }

    let crumbPath = currentPath;
    if (segment === "video" && pathSegments[0] === "training" && index < pathSegments.length - 1) {
      crumbPath = "/training/my-training";
    }

    breadcrumb.push({
      name,
      path: crumbPath,
      current: index === pathSegments.length - 1 && !String(search).includes("tab="),
    });

    if (segment === "settings" && pathSegments.includes("ticketing") && index === pathSegments.length - 1) {
      appendTicketingSettingsTab(breadcrumb, currentPath, search, t);
    }
  });

  if (pathSegments.length === 1 && pathSegments[0] === "ticketing" && breadcrumb[0]) {
    breadcrumb[0].current = false;
    breadcrumb.push({
      name: t("ticketingList", { defaultValue: "Ticket list" }),
      path: "/ticketing",
      current: true,
    });
  }

  return breadcrumb;
}
